// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  Role,
  AuthState,
  AuthData,
  AuthFlowState,
  EmployeeInfo,
  RequestState,
} from "@utils/types";
import { getUserPrivileges } from "@utils/auth";
import {
  AppConfig,
  employeeRole,
  hrRole,
  marketingRole,
  adminRole,
} from "@config/config";
import { APIService } from "@utils/apiService";

const initialState: AuthState = {
  isAuthenticated: false,
  status: "idle",
  statusMessage: null,
  userInfo: null,
  employeeInfo: null,
  employeeInfoRequestState: RequestState.IDLE,
  idToken: null,
  isIdTokenExpired: null,
  decodedIdToken: null,
  roles: [],
  userPrivileges: null,
  errorMessage: null,
  authFlowState: "start",
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUserAuthData: (state, action: PayloadAction<AuthData>) => {
      state.userInfo = action.payload.userInfo;
      state.idToken = action.payload.idToken;
      state.decodedIdToken = action.payload.decodedIdToken;
      let roles = [];
      if (action.payload.decodedIdToken?.groups?.includes(hrRole)) {
        roles.push(Role.HR_ROLE);
      }
      if (action.payload.decodedIdToken?.groups?.includes(marketingRole)) {
        roles.push(Role.MARKETING_ROLE);
      }
      if (action.payload.decodedIdToken?.groups?.includes(employeeRole)) {
        roles.push(Role.EMPLOYEE_ROLE);
      }
      if (action.payload.decodedIdToken?.groups?.includes(adminRole)) {
        roles.push(Role.ADMIN_ROLE);
      }
      state.roles = roles;
    },
    setStatus: (state, action: PayloadAction<AuthState["status"]>) => {
      state.status = action.payload;
    },
    setStatusMessage: (state, action: PayloadAction<string | null>) => {
      state.statusMessage = action.payload;
    },
    setTokenState: (state) => {
      state.isIdTokenExpired = true;
    },
    setAuthFlowState: (state, action: PayloadAction<AuthFlowState>) => {
      state.authFlowState = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.errorMessage = action.payload;
    },
    checkTokenState: (state) => {
      state.isIdTokenExpired = state.decodedIdToken
        ? Date.now() >= state.decodedIdToken?.exp * 1000
        : null;
    },
    resetStates: (state) => {
      state = {
        isAuthenticated: false,
        status: "idle",
        statusMessage: null,
        userInfo: null,
        employeeInfo: null,
        employeeInfoRequestState: RequestState.IDLE,
        idToken: null,
        isIdTokenExpired: null,
        decodedIdToken: null,
        roles: [],
        userPrivileges: null,
        errorMessage: null,
        authFlowState: "start",
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadPrivileges.fulfilled, (state, action) => {
        state.userPrivileges = action.payload;
        state.authFlowState = "end";
        state.isAuthenticated = true;
        state.status = "success";
      })
      .addCase(loadPrivileges.rejected, (state) => {
        state.status = "failed";
        state.authFlowState = "e_user_privileges";
        state.errorMessage = "Unable to load user privileges";
        state.isAuthenticated = false;
      });
    builder
      .addCase(getUserProfileInfo.pending, (state) => {
        state.employeeInfoRequestState = RequestState.LOADING;
      })
      .addCase(
        getUserProfileInfo.fulfilled,
        (state, action: PayloadAction<EmployeeInfo>) => {
          state.employeeInfo = action.payload;
          const roles = state.roles;
          if (state.employeeInfo.lead) {
            roles.push(Role.LEAD_ROLE);
          }
          if (state.employeeInfo.isRecruiter) {
            roles.push(Role.HIRING_MANAGER_ROLE);
          }
          state.employeeInfoRequestState = RequestState.SUCCEEDED;
        },
      )
      .addCase(getUserProfileInfo.rejected, (state) => {
        state.employeeInfoRequestState = RequestState.FAILED;
      });
  },
});

export const loadPrivileges = createAsyncThunk(
  "auth/loadPrivileges",
  async () => {
    return getUserPrivileges();
  },
);

export const getUserProfileInfo = createAsyncThunk(
  "auth/getUserProfileInfo",
  async () => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.userData)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const {
  setIsAuthenticated,
  setUserAuthData,
  setStatus,
  setStatusMessage,
  checkTokenState,
  setTokenState,
  resetStates,
  setAuthFlowState,
  setErrorMessage,
} = authSlice.actions;

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectUserInfo = (state: RootState) => state.auth.userInfo;
export const selectDecodedIdToken = (state: RootState) =>
  state.auth.decodedIdToken;
export const selectIdToken = (state: RootState) => state.auth.idToken;

export const selectStatus = (state: RootState) => state.auth.status;
export const selectRoles = (state: RootState) => state.auth.roles;
export const selectStatusMessage = (state: RootState) =>
  state.auth.statusMessage;
export const isIdTokenExpired = (state: RootState) =>
  state.auth.isIdTokenExpired;

export default authSlice.reducer;
